"use client";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useLocale, formatNumber } from "@/i18n/format";
import { Badge } from "@/components/ui/badge";
import type { PlatformRow } from "./daily-trend-chart";

interface PlatformMixProps {
  byPlatform: PlatformRow[];
  addon: { available: boolean; missingProviders: string[] };
}

const COLORS = [
  "#6366f1",
  "#ec4899",
  "#10b981",
  "#f59e0b",
  "#3b82f6",
  "#8b5cf6",
  "#ef4444",
];

export function PlatformMix({ byPlatform, addon }: PlatformMixProps) {
  const { t } = useTranslation("analytics");
  const locale = useLocale();
  const missing = new Set(addon?.missingProviders ?? []);
  const data = byPlatform.map((p, i) => ({
    ...p,
    color: COLORS[i % COLORS.length],
    isMissing: missing.has(p.platform),
  }));

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="mb-2 text-sm font-medium">{t("platformMix.title")}</h3>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="impressions"
              nameKey="platform"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
            >
              {data.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={entry.isMissing ? "transparent" : entry.color}
                  stroke={entry.color}
                  strokeWidth={entry.isMissing ? 2 : 1}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, _name, props) => {
                const v = typeof value === "number" ? value : Number(value);
                const share = (props?.payload as { sharePct?: number } | undefined)?.sharePct;
                return [
                  `${formatNumber(Number.isFinite(v) ? v : 0, locale)} (${(share ?? 0).toFixed(1)}%)`,
                  t("platformMix.impressions"),
                ];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="mt-3 flex flex-wrap gap-3 text-xs">
        {data.map((p) => (
          <li key={p.platform} className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{
                backgroundColor: p.isMissing ? "transparent" : p.color,
                border: `1.5px solid ${p.color}`,
              }}
            />
            <span className="font-medium capitalize">{p.platform}</span>
            <span className="text-muted-foreground">
              {p.sharePct.toFixed(1)}%
            </span>
            {p.isMissing && (
              <Badge variant="secondary" className="ml-1 text-[10px]">
                {t("platformMix.noAnalytics")}
              </Badge>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
